import { ENTERPRISE_PARTNER_ID } from '@brighte/enterprise-common/constants';
import { LeadAPI } from '@brighte/enterprise-common/schemas';

import {
  AddressesResult,
  Brand,
  Category,
  CategoryPromotion,
  EnterpriseLead,
  EnterpriseLeadContact,
  FinancialProduct,
  ParentCategory,
  Promotion,
  Vendor,
  VendorDetail,
} from '~/gql/graphql';
import { PromotionContentsResponse } from '~/routes/api+/external+/promotion-contents';

export type OutletContextType = {
  channel: string | null;
  partner?: MarketplacePartner;
  leadId: string | null;
  promotions: Array<string>;
  categories: Array<string>;
  postcode: string | null;
  suburb: string | null;
  synthetic: string | null;
  searchParams: URLSearchParams;
};

export type MarketplaceLead = Pick<EnterpriseLead, 'id' | 'state' | 'postcode' | 'city' | 'promotionCode'> & {
  // The schema now exposes `channel` as a PartnerConfigChannel object; the app works with the
  // channel slug string, which services/lead.ts extracts via `channel?.slug ?? null`.
  channel: string | null;
  categorySlugs: string[];
  contact: Pick<EnterpriseLeadContact, 'firstName' | 'lastName' | 'email' | 'phone'>;
  universalId: {
    uid?: string;
  } | null;
  partner?: string;
  vendorIds?: string[];
  quotesByCategory: Record<string, { leadId: string; vendorId: string; category: string; trackingId: string }[]>;
};

type PartialApprovedFinancialProducts = Pick<FinancialProduct, 'slug'>;
export type PartialMarketplacePromotion = Pick<Promotion, 'title' | 'tag' | 'code' | 'publicDescription'>;

export type MarketplaceVendor = Pick<
  Vendor,
  'tradingName' | 'slug' | 'isLocal' | 'logoUrl' | 'publicId' | 'categories'
> & {
  rating?: Vendor['rating'];
  approvedFinancialProducts?: PartialApprovedFinancialProducts[] | null;
  marketplacePromotions: PartialMarketplacePromotion[];
  sortPosition?: number;
  sortMethod?: string;
  details: Array<
    Omit<VendorDetail, 'addressDetail'> & {
      addressDetail?: { __typename?: 'AddressType' | undefined; suburb: string } | null | undefined;
    }
  >;
};

export type MarketplaceCategories = Pick<ParentCategory, 'id' | 'name' | 'slug'> & {
  subCategories?: Pick<Category, 'id' | 'name' | 'slug'>[] | null;
};

export type MarketplaceManufacturerBrands = Pick<Brand, 'name'>;

// eslint-disable-next-line @typescript-eslint/ban-types
export type MarketplacePartner = ENTERPRISE_PARTNER_ID | (string & {});

export type MarketplaceChannel = string;

export type MarketplacePromotion = Pick<Promotion, 'title' | 'code'> & {
  categories?: Pick<CategoryPromotion, 'categorySlug'>[];
};

export type HygraphPromotion = PromotionContentsResponse['promotions'][number] | null;

// NOTE: 'DEFAULT' is deprecated
export const marketplaceSort = [
  'DISTANCE',
  'PRICE_LOW_TO_HIGH',
  'PRICE_HIGH_TO_LOW',
  'RANKING',
  'RANDOM',
  'DEFAULT',
] as const;
export type MarketplaceSort = (typeof marketplaceSort)[number];

export type CustomerAddress = Pick<AddressesResult, 'state' | 'postcode' | 'suburb'> & {
  addressLine1: string;
  addressLine2?: string;
};

export type PremarketplaceJourney = 'assign' | 'marketplace' | 'success';

export type CustomerFormValues = Omit<LeadAPI, 'attachments' | 'vendors'> &
  CustomerAddress & {
    leadId: string;
    name: string;
    termsAndConditions: boolean;
    channel: MarketplaceChannel;
    partner: string;
    vendors: string;
    /** JSON-encoded LeadAPI attachments array — FormData fields are strings */
    attachments?: string;
    isPartnerCustomerIdVerified?: boolean;
    partnerMarketingConsent?: boolean;
    phoneVerified: boolean;
    journey?: PremarketplaceJourney;
    concessionType?: string;
  };

export type CustomerVendorFormValues = {
  vendorId: string;
  sortPosition: number;
  sortMethod: string;
};
